import { useCallback, useEffect, useMemo, useState } from "react";

import { createFeedbackService } from "@/core/services/factory";
import type { Feedback } from "@/core/services/types/feedback.types";
import { useTranslations } from "@/lib/useTranslations";

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function typeBadgeClass(type: string) {
  const normalized = type.trim().toLowerCase();
  if (normalized === "bug") {
    return "bg-whart-urgent/15 text-whart-urgent";
  }
  if (normalized === "signal") {
    return "bg-whart-upcoming/20 text-amber-800";
  }
  if (normalized === "contactus") {
    return "bg-whart-published/15 text-whart-published";
  }
  if (normalized === "delete_account") {
    return "bg-neutral-100 text-neutral-700";
  }
  return "bg-neutral-100 text-neutral-700";
}

function typeLabelKey(type: string): string {
  switch (type.trim()) {
    case "bug":
      return "types.bug";
    case "signal":
      return "types.signal";
    case "contactUs":
      return "types.contactUs";
    case "delete_account":
      return "types.deleteAccount";
    default:
      return "types.other";
  }
}

export function FeedbacksPanel() {
  const t = useTranslations("feedbacks");
  const feedbackService = useMemo(() => createFeedbackService(), []);

  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingId, setMarkingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (mode: "replace" | "append", pageToken?: string | null) => {
      if (mode === "replace") setLoading(true);
      else setLoadingMore(true);
      setError(null);
      try {
        const result = await feedbackService.list({
          pageToken: pageToken ?? null,
          pageSize: 50,
        });
        setFeedbacks((prev) =>
          mode === "append"
            ? [...prev, ...result.feedbacks]
            : result.feedbacks,
        );
        setNextPageToken(result.nextPageToken);
      } catch {
        setError(t("loadError"));
        if (mode === "replace") setFeedbacks([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [feedbackService, t],
  );

  useEffect(() => {
    void load("replace");
  }, [load]);

  const onMarkSeen = async (id: string) => {
    setMarkingId(id);
    setError(null);
    try {
      const result = await feedbackService.markSeen(id);
      if (!result.success) {
        setError(t("markSeenError"));
        return;
      }
      setFeedbacks((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, seen: true } : item,
        ),
      );
    } catch {
      setError(t("markSeenError"));
    } finally {
      setMarkingId(null);
    }
  };

  const unreadCount = feedbacks.filter((item) => !item.seen).length;

  return (
    <div className="space-y-4">
      {error && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      <div className="overflow-hidden rounded-xl border border-black/10 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-black/10 px-4 py-3">
          <p className="text-sm text-neutral-600">
            {t("resultCount", { count: feedbacks.length })}
          </p>
          {unreadCount > 0 && (
            <span className="rounded-xl bg-whart-urgent/15 px-2.5 py-0.5 text-xs font-medium text-whart-urgent">
              {t("unreadCount", { count: unreadCount })}
            </span>
          )}
        </div>

        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-neutral-500">
            {t("loading")}
          </div>
        ) : feedbacks.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-neutral-500">
            {t("empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[64rem] table-fixed text-left text-sm">
              <colgroup>
                <col className="w-[12rem]" />
                <col />
                <col className="w-[14rem]" />
                <col className="w-[9.5rem]" />
                <col className="w-[6.5rem]" />
                <col className="w-[10.5rem]" />
              </colgroup>
              <thead className="border-b border-black/10 bg-zinc-50 text-xs uppercase tracking-wider text-neutral-500">
                <tr>
                  <th className="px-4 py-3 font-medium">
                    {t("columns.type")}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t("columns.message")}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t("columns.email")}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t("columns.createdAt")}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t("columns.status")}
                  </th>
                  <th className="px-4 py-3 font-medium">
                    {t("columns.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {feedbacks.map((item) => (
                  <tr
                    key={item.id}
                    className={`border-b border-black/5 last:border-0 ${
                      item.seen ? "bg-white" : "bg-amber-50/40"
                    }`}
                  >
                    <td className="px-4 py-3 align-top">
                      <span
                        className={`inline-flex max-w-full whitespace-normal break-words rounded-xl px-2.5 py-1 text-xs font-medium leading-snug ${typeBadgeClass(item.type)}`}
                      >
                        {item.type.trim()
                          ? t(typeLabelKey(item.type))
                          : t("types.other")}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-whart-text">
                      <p className="break-words whitespace-pre-wrap">
                        {item.message || "—"}
                      </p>
                    </td>
                    <td className="px-4 py-3 align-top text-neutral-700">
                      <span className="break-all">
                        {item.email.trim() ? item.email : t("emailUnavailable")}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-neutral-600">
                      <span className="inline-block whitespace-nowrap">
                        {formatDate(item.created_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top">
                      {item.seen ? (
                        <span className="inline-flex whitespace-nowrap rounded-xl bg-whart-published/15 px-2.5 py-1 text-xs text-whart-published">
                          {t("seen")}
                        </span>
                      ) : (
                        <span className="inline-flex whitespace-nowrap rounded-xl bg-whart-urgent/15 px-2.5 py-1 text-xs text-whart-urgent">
                          {t("unseen")}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {!item.seen ? (
                        <button
                          type="button"
                          disabled={markingId === item.id}
                          onClick={() => void onMarkSeen(item.id)}
                          className="inline-flex whitespace-nowrap rounded-xl bg-yellow-100 px-3 py-1.5 text-xs font-medium text-zinc-900 transition hover:bg-amber-100 disabled:opacity-50"
                        >
                          {markingId === item.id
                            ? t("markingSeen")
                            : t("markSeen")}
                        </button>
                      ) : (
                        <span className="text-xs text-neutral-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {nextPageToken && !loading && (
          <div className="border-t border-black/10 px-4 py-3">
            <button
              type="button"
              disabled={loadingMore}
              onClick={() => void load("append", nextPageToken)}
              className="rounded-xl border border-black/10 px-4 py-2 text-sm text-neutral-700 transition hover:bg-neutral-50 disabled:opacity-50"
            >
              {loadingMore ? t("loadingMore") : t("loadMore")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
